import { apiClient } from './apiClient';
import type {
  TalentCommittee,
  CreateTcRequest,
  UpdateTcRequest,
  AddTcAppraiseeRequest,
  AddTcMembersRequest,
  ImportTcResult,
  AppraiseeCandidate,
  MemberCandidate,
} from './types/talentCommittee.types';

export const talentCommitteesApi = {
  getTalentCommittees: async (): Promise<TalentCommittee[]> => {
    try {
      const res = await apiClient.get('/talent-committees');
      return res.data;
    } catch (error) {
      console.error('Failed to fetch talent committees:', error);
      throw error;
    }
  },

  createTalentCommittee: async (request: CreateTcRequest): Promise<TalentCommittee> => {
    try {
      const res = await apiClient.post('/talent-committees', request);
      return res.data;
    } catch (error) {
      console.error('Failed to create talent committee:', error);
      throw error;
    }
  },

  updateTalentCommittee: async (id: number, request: UpdateTcRequest, rowVersion?: string): Promise<TalentCommittee> => {
    try {
      const res = await apiClient.put(`/talent-committees/${id}`, { ...request, rowVersion });
      return res.data;
    } catch (error) {
      console.error('Failed to update talent committee:', error);
      throw error;
    }
  },

  deleteTalentCommittee: async (id: number): Promise<void> => {
    try {
      await apiClient.delete(`/talent-committees/${id}`);
    } catch (error) {
      console.error('Failed to delete talent committee:', error);
      throw error;
    }
  },

  addAppraisee: async (tcId: number, request: AddTcAppraiseeRequest): Promise<void> => {
    try {
      await apiClient.post(`/talent-committees/${tcId}/appraisees`, request);
    } catch (error) {
      console.error('Failed to add appraisee:', error);
      throw error;
    }
  },

  removeAppraisee: async (tcId: number, appraisalId: number): Promise<void> => {
    try {
      await apiClient.delete(`/talent-committees/${tcId}/appraisees/${appraisalId}`);
    } catch (error) {
      console.error('Failed to remove appraisee:', error);
      throw error;
    }
  },

  addMembers: async (tcId: number, request: AddTcMembersRequest): Promise<void> => {
    try {
      await apiClient.post(`/talent-committees/${tcId}/members`, request);
    } catch (error) {
      console.error('Failed to add members:', error);
      throw error;
    }
  },

  removeMember: async (tcId: number, staffId: number): Promise<void> => {
    try {
      await apiClient.delete(`/talent-committees/${tcId}/members/${staffId}`);
    } catch (error) {
      console.error('Failed to remove member:', error);
      throw error;
    }
  },

  getTemplate: async (): Promise<Blob> => {
    try {
      const res = await apiClient.get('/talent-committees/export', {
        responseType: 'blob',
      });
      return res.data;
    } catch (error) {
      console.error('Failed to download TC template:', error);
      throw error;
    }
  },

  importTalentCommittees: async (file: File): Promise<ImportTcResult> => {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const res = await apiClient.post('/talent-committees/import', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return res.data;
    } catch (error) {
      console.error('Failed to import talent committees:', error);
      throw error;
    }
  },

  getAppraiseeCandidates: async (search?: string): Promise<AppraiseeCandidate[]> => {
    try {
      const params = search ? `?search=${encodeURIComponent(search)}` : '';
      const res = await apiClient.get(`/talent-committees/appraisee-candidates${params}`);
      return res.data;
    } catch (error) {
      console.error('Failed to fetch appraisee candidates:', error);
      throw error;
    }
  },

  getMemberCandidates: async (search?: string): Promise<MemberCandidate[]> => {
    try {
      const params = search ? `?search=${encodeURIComponent(search)}` : '';
      const res = await apiClient.get(`/talent-committees/member-candidates${params}`);
      return res.data;
    } catch (error) {
      console.error('Failed to fetch member candidates:', error);
      throw error;
    }
  },
};
